import Image from "@tiptap/extension-image";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import DropCursor from "@tiptap/extension-dropcursor";
import GapCursor from "@tiptap/extension-gapcursor";
import Placeholder from "@tiptap/extension-placeholder";
import { EditorContent, useEditor, BubbleMenu } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Table as TableIcon,
  Image as ImageIcon,
  Code,
  Quote,
  Heading1,
  Heading2,
  Heading3,
  Trash2,
} from "lucide-react";
import React, { useCallback, useRef } from "react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

interface EmailTemplateEditorProps {
  content: string;
  onUpdate: (content: string) => void;
  onImageUpload?: (file: File) => Promise<string>;
  label?: string;
  disabled?: boolean;
  placeholder?: string;
}

export function EmailTemplateEditor({
  content,
  onUpdate,
  onImageUpload,
  label = "Contenu de l'email",
  disabled = false,
  placeholder = "Rédigez votre template d'email...",
}: EmailTemplateEditorProps) {
  const editorId = `email-template-editor-${React.useId()}`;
  const editorContainerRef = React.useRef<HTMLDivElement>(null);
  const editorRef = useRef<ReturnType<typeof useEditor>>(null);

  const handleImageUpload = useCallback(async (file: File) => {
    if (!onImageUpload || !editorRef.current) return;
    
    try {
      const imageUrl = await onImageUpload(file);
      editorRef.current?.chain().focus().setImage({ src: imageUrl }).run();
    } catch (error) {
      console.error("Erreur lors de l'upload de l'image:", error);
    }
  }, [onImageUpload]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      DropCursor,
      GapCursor,
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: "table-editor",
          style: "border-collapse: collapse; width: 100%; margin: 1rem 0;",
        },
        cellMinWidth: 50,
      }),
      TableRow.configure({
        HTMLAttributes: {
          style: "border: 1px solid #e5e7eb;",
        },
      }),
      TableHeader.configure({
        HTMLAttributes: {
          style: "background-color: #f9fafb; border: 1px solid #e5e7eb; padding: 12px 8px; font-weight: 600; text-align: center;",
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          style: "border: 1px solid #e5e7eb; padding: 12px 8px; min-height: 2rem; vertical-align: top;",
        },
      }),
      Image.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            width: {
              default: null,
              parseHTML: (element: Element) => element.getAttribute('width'),
              renderHTML: (attributes: any) => {
                if (!attributes.width) return {};
                return { width: attributes.width };
              },
            },
            height: {
              default: null,
              parseHTML: (element: Element) => element.getAttribute('height'),
              renderHTML: (attributes: any) => {
                if (!attributes.height) return {};
                return { height: attributes.height };
              },
            },
            alt: {
              default: null,
              parseHTML: (element: Element) => element.getAttribute('alt'),
              renderHTML: (attributes: any) => {
                if (!attributes.alt) return {};
                return { alt: attributes.alt };
              },
            },
            style: {
              default: null,
              parseHTML: (element: Element) => element.getAttribute('style'),
              renderHTML: (attributes: any) => {
                if (!attributes.style) return {};
                return { style: attributes.style };
              },
            },
          };
        },
        addNodeView() {
          return ({ node, getPos, editor: nodeEditor }: { node: any; getPos: (() => number) | undefined; editor: any }) => {
            const img = document.createElement("img");
            const container = document.createElement("div");
            
            container.className = "relative inline-block group max-w-full";
            img.src = node.attrs.src;
            img.alt = node.attrs.alt || "";
            img.className = "max-w-full h-auto cursor-pointer block";
            img.style.maxWidth = "100%";
            
            if (node.attrs.width) img.width = node.attrs.width;
            if (node.attrs.height) img.height = node.attrs.height;
            if (node.attrs.style) img.style.cssText = node.attrs.style;

            // Resize handles
            const resizeHandleRight = document.createElement("div");
            resizeHandleRight.className = "absolute top-1/2 -right-1 w-3 h-8 bg-blue-500 border border-white cursor-ew-resize opacity-0 group-hover:opacity-100 transition-opacity transform -translate-y-1/2";
            
            const resizeHandleLeft = document.createElement("div");
            resizeHandleLeft.className = "absolute top-1/2 -left-1 w-3 h-8 bg-blue-500 border border-white cursor-ew-resize opacity-0 group-hover:opacity-100 transition-opacity transform -translate-y-1/2";
            
            let isResizing = false;
            let startX = 0;
            let startWidth = 0;
            let resizeDirection = "";

            const startResize = (e: MouseEvent, direction: string) => {
              e.preventDefault();
              e.stopPropagation();
              isResizing = true;
              resizeDirection = direction;
              startX = e.clientX;
              startWidth = img.offsetWidth;
              document.addEventListener("mousemove", handleMouseMove);
              document.addEventListener("mouseup", handleMouseUp);
            };

            resizeHandleRight.addEventListener("mousedown", (e) => startResize(e, "right"));
            resizeHandleLeft.addEventListener("mousedown", (e) => startResize(e, "left"));

            const handleMouseMove = (e: MouseEvent) => {
              if (!isResizing) return;
              
              const deltaX = resizeDirection === "right" ? e.clientX - startX : startX - e.clientX;
              const newWidth = Math.max(50, Math.min(800, startWidth + deltaX));
              
              img.style.width = `${newWidth}px`;
              img.style.height = "auto";
            };

            const handleMouseUp = () => {
              if (!isResizing) return;
              isResizing = false;
              
              const newWidth = Number.parseInt(img.style.width, 10);
              
              if (typeof getPos === "function") {
                const pos = getPos();
                if (pos !== undefined) {
                  nodeEditor.commands.updateAttributes("image", {
                    width: newWidth,
                    style: `width: ${newWidth}px; height: auto;`,
                  });
                }
              }
              
              document.removeEventListener("mousemove", handleMouseMove);
              document.removeEventListener("mouseup", handleMouseUp);
            };

            container.appendChild(img);
            container.appendChild(resizeHandleLeft);
            container.appendChild(resizeHandleRight);

            return {
              dom: container,
              update: (updatedNode) => {
                if (updatedNode.type.name !== "image") return false;
                img.src = updatedNode.attrs.src;
                img.alt = updatedNode.attrs.alt || "";
                if (updatedNode.attrs.width) {
                  img.style.width = `${updatedNode.attrs.width}px`;
                }
                if (updatedNode.attrs.height) {
                  img.style.height = `${updatedNode.attrs.height}px`;
                }
                if (updatedNode.attrs.style) {
                  img.style.cssText = updatedNode.attrs.style;
                }
                return true;
              },
            };
          };
        },
      }).configure({
        allowBase64: true,
        HTMLAttributes: {
          style: "max-width: 100%; height: auto; display: block;",
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content,
    shouldRerenderOnTransaction: false,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm max-w-none",
          "focus:outline-none",
          "min-h-[20rem] p-4 w-full",
          "placeholder:text-muted-foreground/70",
          disabled && "opacity-50 cursor-not-allowed",
        ),
        id: editorId,
      },
      handleKeyDown: (_view, event) => {
        // Standard keyboard shortcuts for editing
        if (event.key === "Escape") {
          editor?.commands.focus();
          return true;
        }
        return false;
      },
      handleDrop: (_view, event, _slice, moved) => {
        if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) {
          const file = event.dataTransfer.files[0];
          if (file.type.startsWith("image/")) {
            event.preventDefault();
            handleImageUpload(file);
            return true;
          }
        }
        return false;
      },
      handlePaste: (_view, event, _slice) => {
        const items = Array.from(event.clipboardData?.items || []);
        for (const item of items) {
          if (item.type.startsWith("image/")) {
            const file = item.getAsFile();
            if (file) {
              event.preventDefault();
              handleImageUpload(file);
              return true;
            }
          }
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      try {
        const html = editor.getHTML();
        onUpdate(html);
      } catch (error) {
        console.error("Error updating editor content:", error);
      }
    },
    editable: !disabled,
    onCreate: ({ editor }) => {
      if (content && content !== editor.getHTML()) {
        setTimeout(() => {
          try {
            editor.commands.setContent(content, false);
          } catch (error) {
            console.warn("Failed to set initial content:", error);
          }
        }, 0);
      }
    },
  });

  React.useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  React.useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      const timer = setTimeout(() => {
        try {
          editor.commands.setContent(content, false);
        } catch (error) {
          console.warn("Failed to set editor content:", error);
          setTimeout(() => {
            try {
              editor.commands.setContent(content || "", false);
            } catch (fallbackError) {
              console.error("Failed to set editor content on fallback:", fallbackError);
            }
          }, 100);
        }
      }, 10);
      
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [content, editor]);

  const focusEditor = () => {
    editor?.commands.focus();
  };

  if (!editor) {
    return (
      <div className="w-full min-h-[20rem] animate-pulse bg-muted/20 rounded" />
    );
  }

  return (
    <div className="w-full">
      {label && (
        <div className="text-sm font-medium block text-foreground mb-2">
          <button
            type="button"
            onClick={focusEditor}
            className="bg-transparent border-0 p-0 text-inherit cursor-pointer font-inherit text-left w-full"
            disabled={disabled}
            tabIndex={-1}
          >
            {label}
          </button>
        </div>
      )}
      
      {!disabled && (
        <div className="border rounded-md mb-3">
          <div className="flex flex-wrap items-center gap-1 p-2">
            {/* Formatting */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  className={cn("h-8 px-2", editor.isActive("bold") && "bg-muted")}
                >
                  <Bold className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Gras</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  className={cn("h-8 px-2", editor.isActive("italic") && "bg-muted")}
                >
                  <Italic className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Italique</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  className={cn("h-8 px-2", editor.isActive("underline") && "bg-muted")}
                >
                  <UnderlineIcon className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Souligné</TooltipContent>
            </Tooltip>

            <Separator orientation="vertical" className="h-6 mx-1" />

            {/* Headings */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                  className={cn("h-8 px-2", editor.isActive("heading", { level: 1 }) && "bg-muted")}
                >
                  <Heading1 className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Titre 1</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                  className={cn("h-8 px-2", editor.isActive("heading", { level: 2 }) && "bg-muted")}
                >
                  <Heading2 className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Titre 2</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                  className={cn("h-8 px-2", editor.isActive("heading", { level: 3 }) && "bg-muted")}
                >
                  <Heading3 className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Titre 3</TooltipContent>
            </Tooltip>

            <Separator orientation="vertical" className="h-6 mx-1" />

            {/* Alignment */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => editor.chain().focus().setTextAlign("left").run()}
                  className={cn("h-8 px-2", editor.isActive({ textAlign: "left" }) && "bg-muted")}
                >
                  <AlignLeft className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Aligner à gauche</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => editor.chain().focus().setTextAlign("center").run()}
                  className={cn("h-8 px-2", editor.isActive({ textAlign: "center" }) && "bg-muted")}
                >
                  <AlignCenter className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Centrer</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => editor.chain().focus().setTextAlign("right").run()}
                  className={cn("h-8 px-2", editor.isActive({ textAlign: "right" }) && "bg-muted")}
                >
                  <AlignRight className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Aligner à droite</TooltipContent>
            </Tooltip>

            <Separator orientation="vertical" className="h-6 mx-1" />

            {/* Lists */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  className={cn("h-8 px-2", editor.isActive("bulletList") && "bg-muted")}
                >
                  <List className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Liste à puces</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  className={cn("h-8 px-2", editor.isActive("orderedList") && "bg-muted")}
                >
                  <ListOrdered className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Liste numérotée</TooltipContent>
            </Tooltip>

            <Separator orientation="vertical" className="h-6 mx-1" />

            {/* Table */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                  className="h-8 px-2"
                >
                  <TableIcon className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Insérer un tableau</TooltipContent>
            </Tooltip>


            <Separator orientation="vertical" className="h-6 mx-1" />

            {/* Insert media */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.onchange = (e: Event) => {
                      const target = e.target as HTMLInputElement;
                      const file = target.files?.[0];
                      if (file) handleImageUpload(file);
                    };
                    input.click();
                  }}
                  className="h-8 px-2"
                >
                  <ImageIcon className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Insérer une image</TooltipContent>
            </Tooltip>

            <Separator orientation="vertical" className="h-6 mx-1" />

            {/* Other */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => editor.chain().focus().toggleBlockquote().run()}
                  className={cn("h-8 px-2", editor.isActive("blockquote") && "bg-muted")}
                >
                  <Quote className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Citation</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => editor.chain().focus().toggleCode().run()}
                  className={cn("h-8 px-2", editor.isActive("code") && "bg-muted")}
                >
                  <Code className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Code</TooltipContent>
            </Tooltip>
          </div>
        </div>
      )}
      
      <div
        ref={editorContainerRef}
        className={cn(
          "relative w-full border rounded-md",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
        )}
      >
        {/* Table BubbleMenu */}
        <BubbleMenu
          editor={editor}
          shouldShow={({ editor }: { editor: any }) => editor.isActive("table")}
          tippyOptions={{ 
            duration: 100,
            hideOnClick: false,
            interactive: true,
            placement: 'top',
            maxWidth: 'none',
          }}
          className="flex items-center gap-1 p-2 bg-background border border-border rounded-md shadow-lg z-50 max-w-fit"
        >
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => editor.chain().focus().addColumnBefore().run()}
            className="h-8 px-3 text-xs whitespace-nowrap"
            title="Insérer une colonne à gauche"
          >
            ← Col
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => editor.chain().focus().addColumnAfter().run()}
            className="h-8 px-3 text-xs whitespace-nowrap"
            title="Insérer une colonne à droite"
          >
            Col →
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => editor.chain().focus().deleteColumn().run()}
            className="h-8 px-3 text-xs text-destructive hover:text-destructive whitespace-nowrap"
            title="Supprimer la colonne"
          >
            <Trash2 className="w-3 h-3 mr-1" />
            Col
          </Button>
          
          <div className="w-px h-4 bg-border mx-2" />
          
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => editor.chain().focus().addRowBefore().run()}
            className="h-8 px-3 text-xs whitespace-nowrap"
            title="Insérer une ligne au-dessus"
          >
            ↑ Ligne
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => editor.chain().focus().addRowAfter().run()}
            className="h-8 px-3 text-xs whitespace-nowrap"
            title="Insérer une ligne en-dessous"
          >
            Ligne ↓
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => editor.chain().focus().deleteRow().run()}
            className="h-8 px-3 text-xs text-destructive hover:text-destructive whitespace-nowrap"
            title="Supprimer la ligne"
          >
            <Trash2 className="w-3 h-3 mr-1" />
            Ligne
          </Button>
          
          <div className="w-px h-4 bg-border mx-2" />
          
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => editor.chain().focus().deleteTable().run()}
            className="h-8 px-3 text-xs text-destructive hover:text-destructive whitespace-nowrap"
            title="Supprimer le tableau"
          >
            <Trash2 className="w-3 h-3 mr-1" />
            Tableau
          </Button>
        </BubbleMenu>

        <style>
          {`
            /* Custom styling for placeholders */
            .ProseMirror {
              --placeholder-color: #6366f1;
              --placeholder-bg: #f1f5f9;
              --placeholder-border: #e2e8f0;
            }
            
            /* Style placeholders as code elements */
            .ProseMirror code {
              background-color: var(--placeholder-bg);
              color: var(--placeholder-color);
              padding: 2px 4px;
              border-radius: 4px;
              font-family: ui-monospace, 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
              font-size: 0.9em;
              font-weight: 500;
              border: 1px solid var(--placeholder-border);
              cursor: default;
            }
            
            /* Ensure no link styling appears anywhere */
            .ProseMirror a {
              color: inherit;
              text-decoration: none;
              pointer-events: none;
            }
            
            /* Improve table styling */
            .ProseMirror table.table-editor {
              margin: 1rem 0;
              border-collapse: collapse;
              width: 100%;
              border: 2px solid #e5e7eb;
              border-radius: 6px;
              overflow: hidden;
              position: relative;
            }
            
            .ProseMirror table.table-editor td,
            .ProseMirror table.table-editor th {
              vertical-align: top;
              border: 1px solid #e5e7eb;
              position: relative;
            }
            
            .ProseMirror table.table-editor th {
              background-color: #f9fafb;
              font-weight: 600;
              text-align: center;
            }
            
            /* Make tables easier to select and edit */
            .ProseMirror table.table-editor:hover {
              border-color: #3b82f6;
              box-shadow: 0 0 0 1px #3b82f6;
            }
            
            .ProseMirror table.table-editor.selectedCell td,
            .ProseMirror table.table-editor.selectedCell th {
              background-color: rgba(59, 130, 246, 0.1);
            }
            
            /* Improve cell selection visibility */
            .ProseMirror .selectedCell:after {
              content: "";
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: rgba(59, 130, 246, 0.2);
              pointer-events: none;
            }
            
            /* Make table resizing handles more visible */
            .ProseMirror .column-resize-handle {
              background-color: #3b82f6;
              width: 3px;
            }
          `}
        </style>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
